import type { EvidenceType, FrameworkCode } from "@/api";
import type { BadgeTone } from "@/components/ui/badge";
import { FRAMEWORK_LABEL } from "@/lib/risk-helpers";

/** Format a framework code into its public label. Re-exports
 *  the risk helper so callers don't have to know that the source
 *  table lives in the risk module. */
export function formatFrameworkCode(framework: string): string {
  return FRAMEWORK_LABEL[framework] ?? framework;
}

/** Bahasa Indonesia labels for evidence kinds. */
export const EVIDENCE_TYPE_LABEL: Record<EvidenceType, string> = {
  policy_version: "Versi kebijakan",
  asset: "Aset",
  risk_snapshot: "Snapshot risiko",
  audit_log: "Log audit",
  framework_alignment: "Alignment framework",
};

/** Map a readiness percentage (0–100) to a badge tone so the
 *  posture cards have a consistent colour treatment. Mirrors the
 *  risk score-band defaults so a tenant reading both pages sees
 *  the same colour language. */
export function readinessTone(score: number): BadgeTone {
  if (!Number.isFinite(score)) return "neutral";
  if (score < 40) return "danger";
  if (score < 60) return "warning";
  if (score < 80) return "success";
  return "ocean";
}

/** Format a readiness numeric (0–100) into a string with the
 *  trailing ``%``. */
export function formatReadiness(
  score: number | string | null | undefined
): string {
  if (score === null || score === undefined) return "—";
  const value = typeof score === "string" ? Number(score) : score;
  if (!Number.isFinite(value)) return "—";
  return `${value.toFixed(1)}%`;
}

/** Format a maturity level (1–5) into a Bahasa Indonesia label.
 *  Maturity levels mirror the NIST + ISO 42001 conventions. */
const MATURITY_LEVEL_LABEL: Record<number, string> = {
  1: "1 — Ad-hoc",
  2: "2 — Repeatable",
  3: "3 — Defined",
  4: "4 — Managed",
  5: "5 — Optimised",
};

export function formatMaturityLevel(level: number | null | undefined): string {
  if (level === null || level === undefined) return "—";
  return MATURITY_LEVEL_LABEL[level] ?? String(level);
}

export const KNOWN_FRAMEWORKS: ReadonlyArray<FrameworkCode> = [
  "NIST_AI_RMF",
  "ISO_42001",
  "ISO_27001",
  "EU_AI_ACT",
  "UU_PDP_2022",
  "POJK_11_2022",
  "PP_71_2019",
];
